import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { useEffect } from "react";
import { updatePaymentPlan } from "../../../api/paiementPlans";

type Props = {
  show: boolean;
  plan: any;
  onClose: () => void;
  onSaved?: () => void;
};

const EditPaymentPlanModal = ({ show, onClose, plan, onSaved }: Props) => {
  const createSchema = z.object({
    id: z.string().optional(),
    label: z.string().nonempty("Le libellé du niveau d'étude est requis"),
    amount: z.string().nonempty("Veuillez sélectionner le type de compte"),
    echeance: z.string().nonempty("Le numéro de compte est requis"),
    to_ignore: z.boolean().optional(),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        label: data.label,
        amount: data.amount,
        due_date: data.echeance,
        to_ignore: data.to_ignore ?? false,
      };
      //Envoi des datas pour modification vers le backend
      await updatePaymentPlan(data?.id, finalData);

      // reset(); //On réinitialise le formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Plan de paiement modifié !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // console.log("erreur lors de la modification d'un plan de paiement: ",error);
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //  initialisation des valeurs par défauts du formulaire
  useEffect(() => {
    if (!show) return;
    if (plan) {
      reset({
        id: plan.id,
        label: plan.label,
        amount: plan.amount,
        echeance: plan.due_date,
        to_ignore: plan.to_ignore ?? false,
      });
    }
  }, [show, plan]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Modifier Plan de Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn btn-danger btn-sm"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: 1ère tranche ou Inscription"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Montant (F CFA)
                  </label>
                  <input
                    {...register("amount")}
                    type="text"
                    className={
                      errors.amount ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: 150 000 FCFA"
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">
                      {errors.amount.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Échéance
                  </label>
                  <input
                    {...register("echeance")}
                    type="date"
                    className={
                      errors.echeance
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 150 000 FCFA"
                  />
                  {errors.echeance && (
                    <div className="invalid-feedback">
                      {errors.echeance.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="form-check mb-3">
                  <input
                    {...register("to_ignore")}
                    className="form-check-input"
                    type="checkbox"
                    id="editToIgnoreCheckbox"
                  />
                  <label className="form-check-label" htmlFor="editToIgnoreCheckbox">
                   est non obligatoire ?
                  </label>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditPaymentPlanModal,
  [buildPermission(Resource.PAYMENT_PLAN, Action.UPDATE)],
  "masquer",
);
